import { useEffect, useState, useCallback } from "react";
import { cn } from "../../utils/tailwindUtils";
import { Monitor, Smartphone, Loader2, X, AlertTriangle } from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import { EMAIL_PREVIEW_URL } from "../../constants/api";

/**
 * Shows the email as it will actually be sent.
 *
 * Rendered server-side by the same function the send path calls, not in the
 * browser. A browser preview shows the browser's interpretation of the authored
 * HTML — which is exactly the thing that does not survive an inbox — so it would
 * look right here and wrong on arrival. Going through the API means the preview
 * cannot silently disagree with what ships.
 *
 * The frame widths are the real constraints: 600px is the shell width, 375px is
 * a typical phone viewport, which is where a fixed-width image or table breaks.
 */
export default function EmailPreviewModal({ open, onClose, body_html, subject, preheader }) {
    const { create } = useApiMethods();
    const [device, setDevice] = useState("desktop");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await create(EMAIL_PREVIEW_URL, { body_html, subject, preheader });
            setData(res?.status === "success" ? res.data : null);
        } catch {
            setData(null);
        }
        setLoading(false);
    }, [create, body_html, subject, preheader]);

    useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
             onClick={onClose}>
            <div className="flex h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl dark:bg-gray-900"
                 onClick={e => e.stopPropagation()}>

                <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3 dark:border-gray-700">
                    <div className="min-w-0">
                        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Preview</h3>
                        <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                            {data?.subject || subject || "(no subject)"}
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="flex rounded-lg border border-gray-200 p-0.5 dark:border-gray-700">
                            {[["desktop", Monitor, "Desktop"], ["mobile", Smartphone, "Mobile"]].map(([key, Icon, label]) => (
                                <button key={key} onClick={() => setDevice(key)}
                                        className={cn(
                                            "flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
                                            device === key
                                                ? "bg-blue-600 text-white"
                                                : "text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                                        )}>
                                    <Icon className="h-3.5 w-3.5" /> {label}
                                </button>
                            ))}
                        </div>
                        <button onClick={onClose}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                </div>

                {data?.gmail_clipping_risk && (
                    <div className="flex items-start gap-2 border-b border-amber-200 bg-amber-50 px-5 py-2 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300">
                        <AlertTriangle className="mt-px h-3.5 w-3.5 flex-shrink-0" />
                        <span>
                            This message is {data.size_kb}KB. Gmail clips anything over about 102KB
                            behind a “View entire message” link — everything below the cut, including
                            the footer, is hidden unless the reader expands it.
                        </span>
                    </div>
                )}

                <div className="flex-1 overflow-auto bg-gray-100 p-6 dark:bg-gray-950">
                    {loading ? (
                        <div className="flex h-full items-center justify-center">
                            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                        </div>
                    ) : !data ? (
                        <p className="text-center text-sm text-gray-400">Could not render the preview.</p>
                    ) : (
                        <div className="mx-auto bg-white shadow-sm transition-all"
                             style={{ width: device === "mobile" ? 375 : 640, maxWidth: "100%" }}>
                            <iframe
                                title="Email preview"
                                srcDoc={data.html}
                                sandbox=""
                                className="h-[70vh] w-full border-0"
                            />
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-between border-t border-gray-200 px-5 py-2 text-[11px] text-gray-400 dark:border-gray-700">
                    <span>Rendered by the server — this is the markup that will be sent.</span>
                    {data && <span className="tabular-nums">{data.size_kb} KB</span>}
                </div>
            </div>
        </div>
    );
}
